// Universal layout constants for consistent spacing across the application
export const LAYOUT_CONSTANTS = {
  // Chat system bottom padding - ensures no overlap with floating chat components
  CHAT_BOTTOM_PADDING: 'md:pb-20', // 5rem/80px on desktop - accommodates chat list and minimized chats (mobile: no padding, full viewport)
  
  // Mobile header top padding - ensures no overlap with mobile header bar
  // MobileHeaderBar: fixed top-4 (16px) + py-2 (8px*2) + h-11 (44px) = 76px, using 80px for safety
  MOBILE_HEADER_PADDING: 'pt-20', // 5rem/80px - mobile header clearance
  
  // Page container spacing
  PAGE_HORIZONTAL_PADDING: 'px-4 sm:px-6 lg:px-8',
  PAGE_VERTICAL_PADDING: 'py-6',
  
  // Container classes
  CONTAINER_BASE: 'container mx-auto',
  
  // Animation classes
  PAGE_ANIMATIONS: 'animate-fade-in-up',
  SECTION_ANIMATIONS: 'animate-stagger-fade-in animate-stagger-2'
} as const;

/**
 * Universal page container class generator.
 *
 * IMPORTANT: Height Constraint Pattern
 * - UnifiedLayout's <main> element uses: flex-1 overflow-hidden h-full
 * - This constrains all pages to viewport height (no scroll)
 * - Pages should use: className="h-full flex flex-col"
 * - Mobile gets pt-14 (56px) for header clearance
 * - Desktop/tablet use left sidebar (no top padding)
 * 
 * For full-height pages like Evee AI:
 * - Root: h-full flex flex-col
 * - Content wrapper: flex h-full overflow-hidden
 * - Scrollable sections: Use ScrollArea within constrained containers
 */
export const getPageContainerClasses = (options?: {
  includeChatPadding?: boolean;
  includeHorizontalPadding?: boolean;
  includeVerticalPadding?: boolean;
  includeContainer?: boolean;
  includeAnimations?: boolean;
  customClasses?: string;
}) => {
  const {
    includeChatPadding = true,
    includeHorizontalPadding = true,
    includeVerticalPadding = true,
    includeContainer = true,
    includeAnimations = true,
    customClasses = ''
  } = options || {};

  const classes = [
    includeContainer ? LAYOUT_CONSTANTS.CONTAINER_BASE : '',
    includeHorizontalPadding ? LAYOUT_CONSTANTS.PAGE_HORIZONTAL_PADDING : '',
    includeVerticalPadding ? LAYOUT_CONSTANTS.PAGE_VERTICAL_PADDING : '',
    includeChatPadding ? LAYOUT_CONSTANTS.CHAT_BOTTOM_PADDING : '',
    includeAnimations ? LAYOUT_CONSTANTS.PAGE_ANIMATIONS : '',
    customClasses
  ].filter(Boolean).join(' ');

  return classes;
};

// Full-height page container for pages like EveeAI, ModernChat
export const getFullHeightPageClasses = (options?: {
  includeChatPadding?: boolean;
  customClasses?: string;
}) => {
  const {
    includeChatPadding = true,
    customClasses = ''
  } = options || {};

  const classes = [
    'h-screen',
    includeChatPadding ? LAYOUT_CONSTANTS.CHAT_BOTTOM_PADDING : '',
    customClasses
  ].filter(Boolean).join(' ');

  return classes;
};

// Chat-safe page container for guaranteed clearance above floating chat
// Note: Mobile header padding (pt-20) is handled by UnifiedLayout's <main> element for STANDARD pages
// Full-height pages (e.g., /evee-ai) with custom headers opt out of this padding
// This utility focuses on bottom padding for chat, not top padding for mobile header
export const getChatSafePageClasses = (options?: {
  includeHorizontalPadding?: boolean;
  includeVerticalPadding?: boolean;
  includeContainer?: boolean;
  includeAnimations?: boolean;
  customClasses?: string;
}) => {
  const {
    includeHorizontalPadding = true,
    includeVerticalPadding = true,
    includeContainer = true,
    includeAnimations = true,
    customClasses = ''
  } = options || {};

  const classes = [
    includeContainer ? LAYOUT_CONSTANTS.CONTAINER_BASE : '',
    includeHorizontalPadding ? LAYOUT_CONSTANTS.PAGE_HORIZONTAL_PADDING : '',
    // Only add BOTTOM padding (pb-6) since UnifiedLayout handles top padding (pt-20/80px on mobile)
    includeVerticalPadding ? 'pb-6' : '',
    LAYOUT_CONSTANTS.CHAT_BOTTOM_PADDING, // Always include chat padding
    includeAnimations ? LAYOUT_CONSTANTS.PAGE_ANIMATIONS : '',
    customClasses
  ].filter(Boolean).join(' ');

  return classes;
};

/**
 * Get mobile-optimized full height classes
 * Uses proper mobile viewport handling for chat interfaces
 */
export const getMobileFullHeightClasses = (options?: {
  includeChatPadding?: boolean;
  customClasses?: string;
}) => {
  const { 
    includeChatPadding = true,
    customClasses = '' 
  } = options || {};
  
  const classes = [
    'h-screen', // Fallback for older browsers
    'h-[100dvh]', // Dynamic viewport height for mobile
    'overflow-hidden', // Prevent page-level scrolling
    includeChatPadding ? LAYOUT_CONSTANTS.CHAT_BOTTOM_PADDING : '',
    customClasses
  ].filter(Boolean).join(' ');
  
  return classes;
};